import { defineStore } from "pinia";
import { isNetworkUnavailable } from "@/composables/network/offlineMode";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import { parameterRestClient } from "@/api";
import { useProjectsStore } from "@/store/modules/projects";

const { innerFetch, innerCreate, innerUpdate } = crudGenerator(
  db.models.Parameter
);

export const useParametersStore = defineStore("parameters", {
  state: () => ({
    parametersIsLoading: false,
    selectedCategory: "",
    filteredParameters: [],
  }),
  actions: {
    async fetchParameters(filters = {}) {
      if (isNetworkUnavailable()) return;

      return innerFetch(
        filters,
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.parametersIsLoading = isLoading;
        }
      );
    },

    async setAllParameters() {
      this.parametersIsLoading = true;
      try {
        this.filteredParameters = await db.parameters.toArray();
      } finally {
        this.parametersIsLoading = false;
      }
    },

    async createParameter(form) {
      const parameter = await innerCreate(
        form,
        useProjectsStore().isOfflineState
      );
      this.filteredParameters.push(parameter);
      return parameter;
    },

    async updateParameter(form) {
      const parameter = await innerUpdate(
        form,
        useProjectsStore().isOfflineState
      );
      const index = this.filteredParameters.findIndex(
        ({ uuid }) => parameter.uuid === uuid
      );
      if (index >= 0) this.filteredParameters[index] = parameter;
      return parameter;
    },

    async deleteParameter(parameter) {
      await parameterRestClient.destroy(parameter.uuid);
      await db.parameters.delete(parameter.uuid);

      this.filteredParameters = this.filteredParameters.filter(
        ({ uuid }) => uuid !== parameter.uuid
      );
    },

    async filterParametersList({ name }) {
      this.parametersIsLoading = true;
      const needle = (name ?? "").toLowerCase();

      try {
        this.filteredParameters = await db.parameters
          .filter((item) => (item.name ?? "").toLowerCase().includes(needle))
          .toArray();
      } finally {
        this.parametersIsLoading = false;
      }
    },

    async filterParametersByCategory({ category, categoriesToHide = [] }) {
      if (this.selectedCategory === category) return;
      while (this.parametersIsLoading)
        await new Promise((r) => setTimeout(r, 10));
      this.parametersIsLoading = true;
      try {
        this.selectedCategory = category;
        const parameters = await db.parameters
          .orderBy("order")
          .filter(
            (item) =>
              !item.mutualisations.length &&
              !item.categories.some((itemCategory) =>
                categoriesToHide.includes(itemCategory)
              ) &&
              item.categories.includes(category)
          )
          .toArray();
        this.filteredParameters = parameters;

        return parameters;
      } finally {
        this.parametersIsLoading = false;
      }
    },

    async filterParametersByUuid(uuidList) {
      while (this.parametersIsLoading)
        await new Promise((r) => setTimeout(r, 200));
      return db.parameters
        .orderBy("name")
        .filter((item) => uuidList.includes(item.uuid))
        .toArray();
    },

    async filterParametersBySumPreselections(sumPreselection) {
      while (this.parametersIsLoading)
        await new Promise((r) => setTimeout(r, 200));
      return db.parameters
        .orderBy("name")
        .filter((item) => item.sumPreselections.includes(sumPreselection))
        .toArray();
    },

    // TODO - PH - 26/10/2022 - Replace by a setRelationship in dexie when ManyToMany will be implemented
    async retrieveParameterByUuid(parameterUuid) {
      return db.parameters.get({ uuid: parameterUuid });
    },
  },
});
